import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Track } from '@app/core/models/Track';
import { notOnlySpacesRegExp } from '@app/shared/custom-validators';
import { isEqualFormData } from '@app/shared/utils';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { SchedulerTrack } from '@radioking/scheduler-core';
import {
  ErrorFormFormat,
  FormHelperService,
  MobileSidenavService,
  UiStoreHelperService,
} from '@radioking/shared/common-services';
import { CloseCurrentPanel, DynamicEnabledPanel } from '@radioking/shared/pannel-manager';
import amplitude from 'amplitude-js';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { SchedulerCoreState } from '../../../../../../scheduler-core/src/lib/states/scheduler-core.state';
import { Program } from '../../../models/programs.model';
import {
  EditProgramFailure,
  EditProgramRequest,
  EditProgramSuccess,
  HideIntroExtro,
  SetActionAfterEditProgram,
} from '../../../states/programs.actions';
import { ProgramsState } from '../../../states/programs.state';
import { IntroOrExtro } from '../create-program/create-program.component';

@Component({
  selector: 'rk-edit-program',
  templateUrl: './edit-program.component.html',
  styleUrls: ['./edit-program.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditProgramComponent implements OnInit, OnDestroy, DynamicEnabledPanel {
  @Select(ProgramsState.editingProgram)
  program$: Observable<Program>;

  editProgramForm: UntypedFormGroup;
  editProgramFormErrors: ErrorFormFormat;
  initialFormData = {};

  // Used to detect if opened on mobile
  mobileData: string;
  $addingType: WritableSignal<IntroOrExtro> = signal(null);
  search$ = new BehaviorSubject<string>('');

  isSaving$: Observable<boolean>;
  subscription = new Subscription();

  constructor(
    private readonly formHelper: FormHelperService,
    private readonly fb: UntypedFormBuilder,
    private readonly helper: UiStoreHelperService,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly mobileService: MobileSidenavService,
  ) {}

  ngOnInit(): void {
    this.createForm();
    this.isSaving$ = this.helper.getLoadingState(
      [EditProgramRequest],
      [EditProgramSuccess, EditProgramFailure],
    );
    this.subscription.add(
      this.program$.pipe(filter(p => !!p)).subscribe(program => {
        this.updateForm(program);
      }),
    );

    this.subscription.add(
      this.actions$.pipe(ofActionDispatched(HideIntroExtro)).subscribe(() => {
        this.hideModalOrIntro();
      }),
    );

    this.subscription.add(
      this.actions$.pipe(ofActionDispatched(EditProgramSuccess)).subscribe(() => {
        this.mobileService.close();
      }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  hideModalOrIntro() {
    if (this.$addingType() !== null) {
      this.$addingType.set(null);
    } else {
      this.mobileService.close(true);
    }
  }

  createForm() {
    this.editProgramFormErrors = {
      name: {},
      color: {},
    };
    this.editProgramForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern(notOnlySpacesRegExp)]],
      color: ['', Validators.required],
      intro: null,
      extro: null,
    });

    this.subscription.add(
      this.editProgramForm.valueChanges.subscribe(() => {
        this.editProgramForm.clearValidators();
        this.formHelper.updateErrorMapping(
          this.editProgramForm,
          this.editProgramFormErrors,
        );
      }),
    );
    this.formHelper.updateErrorMapping(this.editProgramForm, this.editProgramFormErrors);
  }

  updateForm(program: Program) {
    this.editProgramForm.setValue({
      name: program.name,
      color: program.color,
      intro: program.idTrackIntro
        ? this.store.selectSnapshot(SchedulerCoreState.trackWithId)(program.idTrackIntro)
        : null,
      extro: program.idTrackExtro
        ? this.store.selectSnapshot(SchedulerCoreState.trackWithId)(program.idTrackExtro)
        : null,
    });
    this.initialFormData = cloneDeep({ ...this.editProgramForm.getRawValue() });
  }

  @Dispatch()
  submit() {
    amplitude.getInstance().logEvent('edit_program');
    const program: Partial<Program> = {
      name: this.editProgramForm.value['name'],
      color: this.editProgramForm.value['color'],
      idTrackIntro: this.editProgramForm.value['intro']?.id,
      idTrackExtro: this.editProgramForm.value['extro']?.id,
    };

    return new EditProgramRequest(program);
  }

  @Dispatch()
  cancel = () => new CloseCurrentPanel(true);

  hasToShowConfirmOnExit(): boolean {
    return true;
  }

  isDirty(): boolean {
    return (
      this.editProgramForm &&
      !this.editProgramForm.pristine &&
      !isEqualFormData(this.editProgramForm.getRawValue(), this.initialFormData)
    );
  }

  setIntro(track: Track | SchedulerTrack) {
    this.editProgramForm.get('intro').setValue(track);
  }

  setExtro(track: Track | SchedulerTrack) {
    this.editProgramForm.get('extro').setValue(track);
  }

  closeAction(action: any) {
    this.store.dispatch(new SetActionAfterEditProgram(action));
    this.submit();
  }

  canSave(): boolean {
    return true;
  }

  openTrackSelector(e: Event, type: IntroOrExtro) {
    (e.target as HTMLInputElement).blur();
    this.$addingType.set(type);
  }

  applyTrack(track: SchedulerTrack) {
    if (this.$addingType() === 'intro') {
      this.setIntro(track);
    } else {
      this.setExtro(track);
    }
    this.$addingType.set(null);
  }
}
